/**
 * Findings API module - CSPM finding/issue lifecycle.
 *
 * Real per-domain split: contains the actual implementation of the findings
 * domain API object, extracted verbatim from the monolithic
 * frontend/src/services/api.ts as part of the ADR-0003 follow-up.
 *
 * Domain objects:
 *   - findingsApi  (list/get/update, suppress/snooze/remediate, evidence,
 *                   audit trail, auto-assign rules, age report,
 *                   posture issues + bulk issue actions)
 */
import { _unwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';
import type {
  CloudProvider,
  SeverityLevel,
  FindingStatus,
  SecurityFinding,
  PaginatedResponse,
} from '../../types';

// -- findingsApi --
export const findingsApi = {
  list: async (params?: {
    account_id?: string;
    policy_id?: string;
    severity?: SeverityLevel;
    status?: FindingStatus;
    provider?: CloudProvider;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<SecurityFinding>> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/findings', params));
  },

  // Migrated to canonical fetch client (services/api/client.ts).
  // Public signature unchanged: throws on non-2xx, returns unwrapped data.
  get: async (id: string): Promise<SecurityFinding> => {
    return _unwrap(await fetchClient.get<SecurityFinding>(`/api/v1/cspm/findings/${id}`));
  },

  updateStatus: async (
    id: string,
    status: FindingStatus,
    notes?: string
  ): Promise<SecurityFinding> => {
    return _unwrap(await fetchClient.patch(`/api/v1/cspm/findings/${id}`, {
      status,
      notes,
    }));
  },

  // Migrated to canonical fetch client (services/api/client.ts).
  getStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/findings/statistics'));
  },

  // Migrated to canonical fetch client (services/api/client.ts).
  getCategories: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/findings/categories'));
  },

  // Migrated to canonical fetch client (services/api/client.ts).
  getAttackPaths: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/findings/attack-paths'));
  },

  // Migrated to canonical fetch client (services/api/client.ts).
  getRiskScore: async (): Promise<any> => {
    return _unwrap(await fetchClient.get<any>('/api/v1/cspm/findings/risk-score'));
  },

  suppress: async (id: string, data: { reason: string; duration_hours?: number }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/findings/${id}/suppress`, data));
  },

  remediate: async (id: string, data?: { auto?: boolean }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/findings/${id}/remediate`, data));
  },

  // Posture Issues (Wiz-style finding aggregation)
  getIssues: async (params?: { severity?: string; category?: string; provider?: string; status?: string; page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/findings/issues', params));
  },

  // Deep-link / shared-link detail fetch. The /issues list (IssuesPage) is
  // backed by the PERSISTED issue store (/api/v1/issues, UUID ids), so a
  // deep-linked /issue/:id must resolve against that same store. The CSPM
  // grouping-engine endpoint (/cspm/findings/issues/{id}) uses synthetic,
  // in-memory ids and 404s on persisted UUIDs — fall back to it only if the
  // canonical lookup misses.
  getIssue: async (id: string): Promise<any> => {
    try {
      return _unwrap(await fetchClient.get<any>(`/api/v1/issues/${id}`));
    } catch (e) {
      return _unwrap(await fetchClient.get<any>(`/api/v1/cspm/findings/issues/${id}`));
    }
  },

  // Single finding by id — powers the /finding/:id deep-link/shared route.
  getFinding: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get<any>(`/api/v1/cspm/findings/${id}`));
  },

  getIssueStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/findings/issues/statistics'));
  },

  // Snooze
  snooze: async (id: string, data: { duration_hours: number; reason: string }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/findings/${id}/snooze`, data));
  },

  // Evidence
  getEvidence: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cspm/findings/${id}/evidence`));
  },

  addEvidence: async (id: string, data: { type: string; content: string; description?: string }): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cspm/findings/${id}/evidence`, data));
  },

  deleteEvidence: async (id: string, evidenceId: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/cspm/findings/${id}/evidence/${evidenceId}`));
  },

  // Audit Trail
  getAuditTrail: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cspm/findings/${id}/audit-trail`));
  },

  // Auto-Assign Rules
  getAutoAssignRules: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/findings/auto-assign/rules'));
  },

  createAutoAssignRule: async (data: { name: string; conditions: any; assignee: string }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cspm/findings/auto-assign/rules', data));
  },

  updateAutoAssignRule: async (id: string, data: any): Promise<any> => {
    return _unwrap(await fetchClient.put(`/api/v1/cspm/findings/auto-assign/rules/${id}`, data));
  },

  deleteAutoAssignRule: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/cspm/findings/auto-assign/rules/${id}`));
  },

  // Age Report
  getAgeReport: async (params?: { severity?: string; provider?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/cspm/findings/age-report', params));
  },

  // R193-SHIP-AB (2026-05-19): findingsApi.{bulkUpdateStatus, export,
  // bulkSuppress, bulkResolve, bulkAssign} removed as dead duplicates of
  // the working FindingsPage/IssuesPage direct-apiClient flows (SHIP-L
  // tagged; SHIP-AB deleted after re-confirming zero callers).
};
